import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { formatSymbolLabel } from '../utils/formatSymbolLabel';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  TrendingUp,
  TrendingDown,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Sparkles,
  Target,
  Trophy,
  Activity,
  Search,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LIMIT = 25;

const emptyFilters = {
  asset_type: 'all',
  profile: 'all',
  direction: 'all',
  status: 'all',
  exit_level: 'all',
  symbol: '',
  sort_by: 'created_at_desc',
};

const PublicTrackRecordJournal = () => {
  const { language } = useLanguage();
  const [data, setData] = useState({ total: 0, trades: [], filters: { asset_types: [], profiles: [], symbols: [] } });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [expandedId, setExpandedId] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const t = (fr, en) => (language === 'fr' ? fr : en);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== 'all') p.set(k, v);
    });
    p.set('limit', String(limit));
    p.set('skip', String(skip));
    return p.toString();
  }, [filters, limit, skip]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/track-record/public/trades?${queryString}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setData({ total: 0, trades: [], filters: { asset_types: [], profiles: [], symbols: [] } });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString]);

  const updateFilter = (key, value) => {
    setSkip(0);
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const resetFilters = () => {
    setSkip(0);
    setFilters(emptyFilters);
  };

  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => v && v !== 'all' && !(k === 'sort_by' && v === 'created_at_desc') && v !== ''
  ).length;

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="public-journal-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <Badge className="bg-primary/10 text-primary border-primary/30 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            {t('Transparence totale', 'Full transparency')}
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3">
            {t('Le journal complet de nos analyses', 'The full journal of our analyses')}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t(
              'Explorez chaque trade proposé par notre IA avec son raisonnement complet, ses niveaux et son résultat. Filtrez par marché, profil, direction, TP atteint ou symbole.',
              'Explore every trade proposed by our AI with full reasoning, levels and result. Filter by market, profile, direction, TP hit or symbol.'
            )}
          </p>
        </div>

        {/* Filters */}
        <Card className="bg-card/50 border-white/10 mb-6" data-testid="journal-filters">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                {t('Filtres', 'Filters')}
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary/20 text-primary border-0 h-5 px-1.5">
                    {activeFilterCount}
                  </Badge>
                )}
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="reset-filters-btn"
                >
                  <X className="w-3 h-3 mr-1" />
                  {t('Réinitialiser', 'Reset')}
                </Button>
              )}
            </div>

            {showFilters && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Symbol search */}
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Symbole', 'Symbol')}
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t('ex: BTC, TSLA...', 'e.g. BTC, TSLA...')}
                      value={filters.symbol}
                      onChange={(e) => updateFilter('symbol', e.target.value)}
                      className="pl-9 bg-background/50"
                      data-testid="filter-symbol-input"
                    />
                  </div>
                </div>

                {/* Asset Type */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Marché', 'Market')}
                  </label>
                  <Select value={filters.asset_type} onValueChange={(v) => updateFilter('asset_type', v)}>
                    <SelectTrigger className="bg-background/50" data-testid="filter-asset-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('Tous', 'All')}</SelectItem>
                      {data.filters.asset_types.map((a) => (
                        <SelectItem key={a} value={a}>
                          {a === 'crypto' && 'Crypto'}
                          {a === 'stocks' && t('Actions', 'Stocks')}
                          {a === 'etf' && 'ETF'}
                          {a === 'forex' && 'Forex'}
                          {a === 'commodities' && t('Matières 1res', 'Commodities')}
                          {a === 'indices' && t('Indices', 'Indices')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Profile */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Profil', 'Profile')}
                  </label>
                  <Select value={filters.profile} onValueChange={(v) => updateFilter('profile', v)}>
                    <SelectTrigger className="bg-background/50" data-testid="filter-profile">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('Tous', 'All')}</SelectItem>
                      {data.filters.profiles.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p === 'prudent' && t('Prudent', 'Conservative')}
                          {p === 'balanced' && t('Équilibré', 'Balanced')}
                          {p === 'dynamic' && t('Dynamique', 'Dynamic')}
                          {!['prudent', 'balanced', 'dynamic'].includes(p) && p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Direction */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Direction', 'Direction')}
                  </label>
                  <Select value={filters.direction} onValueChange={(v) => updateFilter('direction', v)}>
                    <SelectTrigger className="bg-background/50" data-testid="filter-direction">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('Toutes', 'All')}</SelectItem>
                      <SelectItem value="LONG">Long</SelectItem>
                      <SelectItem value="SHORT">Short</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Status */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Résultat', 'Result')}
                  </label>
                  <Select value={filters.status} onValueChange={(v) => updateFilter('status', v)}>
                    <SelectTrigger className="bg-background/50" data-testid="filter-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('Tous', 'All')}</SelectItem>
                      <SelectItem value="WIN">{t('Gagnants', 'Wins')}</SelectItem>
                      <SelectItem value="LOSS">{t('Perdants', 'Losses')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Exit Level */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Sortie TP', 'TP hit')}
                  </label>
                  <Select value={filters.exit_level} onValueChange={(v) => updateFilter('exit_level', v)}>
                    <SelectTrigger className="bg-background/50" data-testid="filter-exit-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('Toutes', 'All')}</SelectItem>
                      <SelectItem value="tp1">TP1 (25%)</SelectItem>
                      <SelectItem value="tp2">TP2 (50%)</SelectItem>
                      <SelectItem value="tp100">TP100 (100%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Sort */}
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">
                    {t('Tri', 'Sort')}
                  </label>
                  <Select value={filters.sort_by} onValueChange={(v) => updateFilter('sort_by', v)}>
                    <SelectTrigger className="bg-background/50" data-testid="filter-sort">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="created_at_desc">{t('Plus récents', 'Most recent')}</SelectItem>
                      <SelectItem value="created_at_asc">{t('Plus anciens', 'Oldest')}</SelectItem>
                      <SelectItem value="pnl_desc">{t('Meilleur PnL', 'Best PnL')}</SelectItem>
                      <SelectItem value="pnl_asc">{t('Pire PnL', 'Worst PnL')}</SelectItem>
                      <SelectItem value="symbol_asc">{t('Symbole (A-Z)', 'Symbol (A-Z)')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result count */}
        <div className="flex items-center justify-between mb-4 text-sm">
          <div className="text-muted-foreground" data-testid="journal-total-count">
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Activity className="w-4 h-4 animate-spin" />
                {t('Chargement...', 'Loading...')}
              </span>
            ) : (
              <>
                <span className="font-semibold text-foreground">{data.total}</span>{' '}
                {t('analyse(s) correspondante(s)', 'matching analyses')}
              </>
            )}
          </div>
          <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setSkip(0); }}>
            <SelectTrigger className="w-32 bg-background/50 h-9" data-testid="filter-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 / {t('page', 'page')}</SelectItem>
              <SelectItem value="50">50 / {t('page', 'page')}</SelectItem>
              <SelectItem value="100">100 / {t('page', 'page')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Trade list */}
        {!loading && data.trades.length === 0 ? (
          <Card className="bg-card/50 border-white/10">
            <CardContent className="p-12 text-center text-muted-foreground">
              {t('Aucun trade ne correspond à ces filtres.', 'No trade matches these filters.')}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="journal-trades-list">
            {data.trades.map((trade) => (
              <TradeRow
                key={trade.id}
                trade={trade}
                expanded={expandedId === trade.id}
                onToggle={() => setExpandedId(expandedId === trade.id ? null : trade.id)}
                t={t}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {data.total > limit && (
          <div className="flex items-center justify-center gap-2 mt-6" data-testid="journal-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              data-testid="pagination-prev-btn"
            >
              {t('Précédent', 'Previous')}
            </Button>
            <span className="text-sm text-muted-foreground px-3">
              {t('Page', 'Page')} {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={skip + limit >= data.total}
              onClick={() => setSkip(skip + limit)}
              data-testid="pagination-next-btn"
            >
              {t('Suivant', 'Next')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

const TradeRow = ({ trade, expanded, onToggle, t, formatDate }) => {
  const isWin = trade.status === 'WIN';
  const isLoss = trade.status === 'LOSS';
  const isClosed = isWin || isLoss;
  const pnl = trade.pnl ?? 0;
  const pnlPct = trade.pnl_percentage ?? 0;
  const dirIsLong = (trade.direction || '').toUpperCase() === 'LONG';

  const statusColor = isWin
    ? 'bg-green-500/10 text-green-400 border-green-500/30'
    : isLoss
    ? 'bg-red-500/10 text-red-400 border-red-500/30'
    : 'bg-blue-500/10 text-blue-400 border-blue-500/30';

  const statusLabel = isWin
    ? 'WIN'
    : isLoss
    ? 'LOSS'
    : trade.status === 'active'
    ? t('En cours', 'Active')
    : trade.status === 'pending'
    ? t('En attente', 'Pending')
    : trade.status;

  const dirColor = dirIsLong ? 'text-green-400' : 'text-red-400';

  return (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <Card className={`bg-card/70 border-white/10 hover:border-white/20 transition-colors overflow-hidden ${expanded ? 'border-primary/30' : ''}`}>
        <CollapsibleTrigger asChild>
          <button
            className="w-full text-left"
            data-testid={`trade-row-${trade.id}`}
          >
            <CardContent className="p-4 sm:p-5">
              <div className="grid grid-cols-12 gap-3 items-center">
                {/* Symbol + direction */}
                <div className="col-span-6 sm:col-span-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isWin ? 'bg-green-500/10' : isLoss ? 'bg-red-500/10' : 'bg-white/5'}`}>
                      {dirIsLong ? (
                        <TrendingUp className={`w-4 h-4 ${dirColor}`} />
                      ) : (
                        <TrendingDown className={`w-4 h-4 ${dirColor}`} />
                      )}
                    </div>
                    <div>
                      <div className="font-mono font-semibold text-sm sm:text-base">{formatSymbolLabel(trade.symbol)}</div>
                      <div className={`text-xs ${dirColor} font-medium`}>{trade.direction}</div>
                    </div>
                  </div>
                </div>

                {/* Date + profile */}
                <div className="hidden sm:block sm:col-span-2">
                  <div className="text-xs text-muted-foreground">{formatDate(trade.closed_at || trade.created_at)}</div>
                  {trade.profile && (
                    <div className="text-xs mt-0.5 capitalize">
                      {trade.profile === 'prudent' && t('Prudent', 'Conservative')}
                      {trade.profile === 'balanced' && t('Équilibré', 'Balanced')}
                      {trade.profile === 'dynamic' && t('Dynamique', 'Dynamic')}
                      {trade.leverage && <span className="text-muted-foreground"> · {trade.leverage}x</span>}
                    </div>
                  )}
                </div>

                {/* Entry / Exit */}
                <div className="hidden sm:block sm:col-span-3">
                  <div className="text-xs text-muted-foreground">
                    {t('Entrée', 'Entry')}: <span className="font-mono text-foreground">{trade.entry_price}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t('Sortie', 'Exit')}: <span className="font-mono text-foreground">{trade.exit_price ?? '—'}</span>
                  </div>
                </div>

                {/* PnL */}
                <div className="col-span-4 sm:col-span-2 text-right">
                  {isClosed ? (
                    <>
                      <div className={`font-mono font-bold text-base sm:text-lg ${pnl > 0 ? 'text-green-400' : pnl < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                        {pnl > 0 ? '+' : ''}
                        {pnl.toFixed(2)}$
                      </div>
                      {pnlPct !== 0 && (
                        <div className={`text-xs ${pnlPct > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {pnlPct > 0 ? '+' : ''}
                          {pnlPct.toFixed(1)}%
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">
                      {t('Non clôturé', 'Not closed')}
                    </div>
                  )}
                </div>

                {/* Status badge */}
                <div className="col-span-2 sm:col-span-2 flex items-center justify-end gap-2">
                  <Badge className={`${statusColor} border`}>
                    {isWin && <Trophy className="w-3 h-3 mr-1" />}
                    {statusLabel}
                  </Badge>
                  {expanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>
              </div>
            </CardContent>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-white/5 bg-black/20 p-4 sm:p-6 space-y-4" data-testid={`trade-details-${trade.id}`}>
            {/* Grid of trade details */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
              <DetailField label={t("Type", "Type")} value={trade.trade_type} />
              <DetailField
                label={t('Profil', 'Profile')}
                value={
                  trade.profile === 'prudent'
                    ? t('Prudent', 'Conservative')
                    : trade.profile === 'balanced'
                    ? t('Équilibré', 'Balanced')
                    : trade.profile === 'dynamic'
                    ? t('Dynamique', 'Dynamic')
                    : trade.profile
                }
              />
              <DetailField label={t('Levier', 'Leverage')} value={trade.leverage ? `${trade.leverage}x` : null} />
              <DetailField label={t('R:R', 'R:R')} value={trade.risk_reward_ratio ? `${trade.risk_reward_ratio}:1` : null} />
              <DetailField label={t('Entrée', 'Entry')} value={trade.entry_price} mono />
              <DetailField label={t('Stop Loss', 'Stop Loss')} value={trade.stop_loss} mono colorClass="text-red-400" />
              <DetailField label="TP1 (25%)" value={trade.take_profit_1} mono colorClass="text-green-400" />
              <DetailField label="TP2 (50%)" value={trade.take_profit_2} mono colorClass="text-green-400" />
              <DetailField label="TP100" value={trade.take_profit_100} mono colorClass="text-emerald-400" />
              <DetailField label={t('Sortie', 'Exit')} value={trade.exit_price} mono />
              <DetailField label={t('Créé le', 'Created')} value={formatDate(trade.created_at)} />
              <DetailField label={t('Clôturé le', 'Closed')} value={formatDate(trade.closed_at)} />
              {trade.ai_confidence != null && (
                <DetailField
                  label={t('Confiance IA', 'AI Confidence')}
                  value={`${trade.ai_confidence}/10`}
                  colorClass="text-primary"
                />
              )}
              {trade.auto_scan_created && (
                <DetailField
                  label={t('Origine', 'Origin')}
                  value={<Badge className="bg-primary/10 text-primary border-primary/30"><Target className="w-3 h-3 mr-1" />Auto-Scan</Badge>}
                />
              )}
            </div>

            {/* AI analysis */}
            {trade.ai_analysis && Object.values(trade.ai_analysis).some((v) => v) && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">{t("Analyse IA (ICT)", 'AI Analysis (ICT)')}</span>
                </div>

                {trade.ai_analysis.reason && (
                  <div className="mb-3">
                    <div className="text-xs text-muted-foreground mb-1">{t("Raisonnement", 'Reasoning')}</div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{trade.ai_analysis.reason}</p>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  {trade.ai_analysis.structure && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('Structure', 'Structure')}</div>
                      <p className="text-sm">{trade.ai_analysis.structure}</p>
                    </div>
                  )}
                  {trade.ai_analysis.weekly_bias && (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">{t('Biais hebdo', 'Weekly bias')}</div>
                      <p className="text-sm">{trade.ai_analysis.weekly_bias}</p>
                    </div>
                  )}
                </div>

                {trade.ai_analysis.key_levels && typeof trade.ai_analysis.key_levels === 'object' && (
                  <div className="mt-3">
                    <div className="text-xs text-muted-foreground mb-1">{t('Niveaux clés', 'Key levels')}</div>
                    <div className="flex flex-wrap gap-2 text-xs font-mono">
                      {Object.entries(trade.ai_analysis.key_levels).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="border-white/10">
                          {k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {trade.ai_analysis.charts_analyzed && Array.isArray(trade.ai_analysis.charts_analyzed) && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    {t('Timeframes analysés', 'Timeframes analyzed')}:{' '}
                    <span className="font-mono">{trade.ai_analysis.charts_analyzed.join(', ')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Fallback notes if no AI analysis */}
            {!trade.ai_analysis && trade.notes && (
              <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">{t('Notes', 'Notes')}</span>
                </div>
                <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed text-muted-foreground">
                  {trade.notes}
                </pre>
              </div>
            )}

            {!trade.ai_analysis && !trade.notes && (
              <p className="text-xs text-muted-foreground italic">
                {t(
                  'Aucun raisonnement IA détaillé archivé pour ce trade.',
                  'No detailed AI reasoning archived for this trade.'
                )}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const DetailField = ({ label, value, mono = false, colorClass = '' }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-0.5">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} ${colorClass || 'text-foreground'}`}>{value}</div>
    </div>
  );
};

export default PublicTrackRecordJournal;
